import { Form, Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { MediumButton, MediumPrimaryButton } from '../../components/Buttons';
import { FormField } from '../../components/FormElements';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from '../../components/ModalElements';
import alertService from '../../services/AlertService';
import { addFeedbackResponseUser } from './api/addFeedbackResponseUser';
import { Step } from '../steps';
import { useUser } from '../authentication';
import { ErrorMessage } from '../../components/AlertMessages';

const schema = Yup.object({
  firstName: Yup.string().max(64).required('A name is required'),
  email: Yup.string()
    .matches(/^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9-]+\.[A-Za-z0-9.]+$/, 'A valid email is required')
    .required('A valid email is required'),
});

type Values = { firstName: string; email: string };

type Props = {
  courseId: string;
  step: Step;
  onAddRequest: (values: Values) => void;
  handleDismiss: () => void;
};

export default function AddFeedbackRecipientModal({ courseId, step, onAddRequest, handleDismiss }: Props) {
  const { user } = useUser();
  const userId = user?.userId;
  const [error, setError] = useState<boolean>(false);

  const handleSubmit = (values: Values, { setSubmitting, setStatus }: FormikHelpers<Values>) => {
    setError(false);
    if (!courseId || !step || !userId) {
      setSubmitting(false);
      return;
    }
    setStatus(undefined);
    addFeedbackResponseUser(courseId, step, values.email, values.firstName, window.location.origin, userId)
      .then(() => {
        onAddRequest(values);
        alertService.show('Feedback Request Sent', 'success');
        handleDismiss();
      })
      .catch(err => {
        console.error(err);
        setError(true);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader>
        <h3>Add Feedback Recipient</h3>
      </ModalHeader>
      <Formik initialValues={{ firstName: '', email: '' }} validationSchema={schema} onSubmit={handleSubmit}>
        {({ isValid, dirty, isSubmitting }) => (
          <Form>
            <ModalBody>
              <FormField
                data-qa-hook="addContactName"
                label="First Name"
                name="firstName"
                type="text"
                placeholder="Enter First Name"
                maxLength={64}
              />
              <FormField
                data-qa-hook="addContactEmail"
                label="Email"
                name="email"
                type="email"
                placeholder="Enter Email"
              />
              {error && <ErrorMessage>We couldn&apos;t send the feedback request. Please try again.</ErrorMessage>}
            </ModalBody>
            <ModalFooter>
              <MediumPrimaryButton
                type="submit"
                data-qa-hook="addContactSubmit"
                disabled={!isValid || !dirty || isSubmitting}
                operating={isSubmitting}
              >
                Send Feedback Request
              </MediumPrimaryButton>
              <MediumButton data-qa-hook="addContactCancel" onClick={handleDismiss}>
                Cancel
              </MediumButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </ModalContainer>
  );
}
